import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaUserShield } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const SCSSCalculator = () => {
  const [principal, setPrincipal] = useState(1500000);
  const [interestRate, setInterestRate] = useState(8.2); // Current SCSS rate
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, interestRate]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const p = Number(principal) || 0;
    const r = Number(interestRate) || 0;
    
    // SCSS: 5-year tenure, interest paid quarterly
    const tenure = 5;
    const rate = r / 100;
    const quarterlyInterest = (p * rate) / 4;
    const annualInterest = p * rate;
    
    const quarterlyData = [];
    for (let quarter = 1; quarter <= tenure * 4; quarter++) {
      quarterlyData.push({
        quarter,
        year: Math.ceil(quarter / 4),
        interestPaid: Math.round(quarterlyInterest),
      });
    }
    
    const totalInterest = annualInterest * tenure;
    const totalPayout = p + totalInterest;
    
    setResult({
      quarterlyInterest: Math.round(quarterlyInterest),
      annualInterest: Math.round(annualInterest),
      monthlyEquivalent: Math.round(annualInterest / 12),
      totalInterest: Math.round(totalInterest),
      totalPayout: Math.round(totalPayout),
      quarterlyData,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Senior Citizens Savings Scheme Report', {
      'Investment Amount': formatCurrency(principal),
      'Interest Rate': `${interestRate}% p.a.`,
      'Tenure': '5 years',
      'Quarterly Interest': formatCurrency(result.quarterlyInterest),
      'Annual Interest': formatCurrency(result.annualInterest),
      'Monthly Equivalent': formatCurrency(result.monthlyEquivalent),
      'Total Interest (5 years)': formatCurrency(result.totalInterest),
      'Total Payout': formatCurrency(result.totalPayout),
    });
  };

  const handleReset = () => {
    setPrincipal(1500000);
    setInterestRate(8.2);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="SCSS Calculator"
        description="Free Senior Citizen Savings Scheme Calculator - Calculate SCSS returns. Best rates for senior citizens."
        keywords="SCSS calculator, Senior Citizen Savings Scheme calculator, SCSS interest rate, SCSS maturity calculator, senior citizen investment"
        canonicalPath="/scss"
      />
      <div className="calculator-header">
        <h1><FaUserShield style={{ color: '#6366f1' }} /> Senior Citizens Savings Scheme (SCSS)</h1>
        <p>Calculate quarterly interest income for senior citizens (60+ years)</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Investment Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal || ''}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Math.min(Number(e.target.value), 3000000))}
                min="1000"
                max="3000000"
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>Min: ₹1,000, Max: ₹30 lakhs</small>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="3000000"
                step="10000"
                value={principal || 0}
                onChange={(e) => setPrincipal(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={interestRate || ''}
              onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="15"
              step="0.1"
            />
            <small style={{ color: 'var(--text-secondary)' }}>Current rate: 8.2% (Q1 FY 2024-25) - Highest among govt schemes</small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                <h3>Quarterly Interest Income</h3>
                <div className="amount">{formatCurrency(result.quarterlyInterest)}</div>
                <small>Paid every quarter to your account</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Annual Interest</span>
                  <span className="value" style={{ color: '#2563eb', fontSize: '1.2em' }}>{formatCurrency(result.annualInterest)}</span>
                </div>
                <div className="result-item" style={{ background: '#f0fdf4', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Monthly Equivalent</span>
                  <span className="value" style={{ color: '#16a34a', fontSize: '1.2em' }}>{formatCurrency(result.monthlyEquivalent)}</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Invested</span>
                  <span className="value">{formatCurrency(principal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Interest (5 years)</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.totalInterest)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Payout (Principal + Interest)</span>
                  <span className="value" style={{ fontWeight: 'bold' }}>{formatCurrency(result.totalPayout)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Rate</span>
                  <span className="value">{interestRate}% p.a.</span>
                </div>
              </div>

              {/* Quarterly Payout Schedule */}
              <div className="breakup-section">
                <h4>Quarterly Interest Payout Schedule</h4>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  <table className="tax-slab-table">
                    <thead>
                      <tr>
                        <th>Quarter</th>
                        <th>Year</th>
                        <th>Interest Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.quarterlyData.map((row, index) => (
                        <tr key={index} style={{ background: row.quarter % 4 === 0 ? '#f0fdf4' : 'inherit' }}>
                          <td>Q{((row.quarter - 1) % 4) + 1}</td>
                          <td>Year {row.year}</td>
                          <td style={{ color: '#10b981', fontWeight: 'bold' }}>{formatCurrency(row.interestPaid)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: '#dbeafe', fontWeight: 'bold' }}>
                        <td colSpan="2">Total Interest (5 years)</td>
                        <td style={{ color: '#2563eb' }}>{formatCurrency(result.totalInterest)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 SCSS Key Features</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li>For individuals aged 60+ years</li>
              <li>55+ for retired defence/civilian employees</li>
              <li>Min: ₹1,000, Max: ₹30 lakhs</li>
              <li>5-year tenure (extendable by 3 years)</li>
              <li>Interest paid quarterly</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>Highest interest rate among govt schemes</li>
              <li>Section 80C benefit up to ₹1.5 lakh</li>
              <li>TDS if interest {'>'} ₹50,000/year</li>
              <li>Premature withdrawal allowed with penalty</li>
              <li>Available at Post Offices & banks</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SCSSCalculator;
